/**
 * SessionListItem - Compact session row for All Sessions list
 *
 * Shows session name, status badge, and Monitor button
 */

import { Group, Badge, Button, Text, Box } from '@mantine/core'
import { IconBell, IconCheck } from '@tabler/icons-react'
import type { SessionInfo } from '../hooks/useSessionMonitor'

interface SessionListItemProps {
  session: SessionInfo
  isMonitored: boolean
  onMonitor: () => void
}

export function SessionListItem({ session, isMonitored, onMonitor }: SessionListItemProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'idle': return 'gray'
      case 'active': return 'blue'
      case 'waiting': return 'yellow'
      case 'stuck': return 'red'
      case 'dead': return 'dark'
      default: return 'gray'
    }
  }

  return (
    <Box
      py="xs"
      px="sm"
      style={{
        borderBottom: '1px solid var(--mantine-color-dark-5)',
      }}
    >
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <Text
            fw={500}
            size="sm"
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {session.name}
          </Text>
          <Badge
            size="xs"
            color={getStatusColor(session.status)}
            variant={session.status === 'waiting' || session.status === 'stuck' ? 'filled' : 'light'}
          >
            {session.status.toUpperCase()}
          </Badge>
          {session.detail && (
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
              {session.detail}
            </Text>
          )}
        </Group>

        {isMonitored ? (
          <Badge
            size="sm"
            variant="light"
            color="violet"
            leftSection={<IconCheck size={12} />}
          >
            Monitored
          </Badge>
        ) : (
          <Button
            size="xs"
            variant="light"
            color="violet"
            leftSection={<IconBell size={14} />}
            onClick={onMonitor}
          >
            Monitor
          </Button>
        )}
      </Group>
    </Box>
  )
}
